import { surfaceColors } from '@/colors';
import { classnames } from '@/helpers';
import { customTwMerge } from '@/helpers/theme';
import moment from 'moment';
import { DateRange } from '../DatePicker.types';
import { Day as DayType, isDateWithinRange, isRangeEnd, isRangeStart } from '../helpers';

type DayProps = {
  day: DayType;
  selectedStart: DateRange['start'];
  selectedEnd: DateRange['end'];
  onSelect: (date: Date) => void;
};

const Day = ({ day, selectedStart, selectedEnd, onSelect }: DayProps) => {
  const { date, isCurrentMonth, isToday, isFirstDayOfCurrentMonth, isLastDayOfCurrentMonth } = day;
  const mDate = moment(date);

  const isInRange = isDateWithinRange(date, selectedStart, selectedEnd);
  const isStart = isRangeStart(date, selectedStart);
  const isEnd = isRangeEnd(date, selectedEnd);
  const isSelected = isStart || isEnd;
  const isStartOfWeek = date.getDay() === 1; // Monday
  const isEndOfWeek = date.getDay() === 0; // Sunday
  const isInPast = moment(date).isBefore(moment(), 'day');
  const disabled = !isCurrentMonth || isInPast;

  const dayClasses = customTwMerge(
    classnames(
      'w-full h-[48px] flex items-center justify-center text-l cursor-pointer text-fg_primary ',
      'outline-none focus-visible:outline focus-visible:outline-border_focus',
    ),
    !isCurrentMonth && 'invisible',
    isToday && 'font-semibold',
    day.isSunday && 'text-fg_highlight',
    /**
     * Range selection styles
     */
    isStart && 'text-fg_primary_inverse !rounded-l-full',
    isEnd && 'text-fg_primary_inverse !rounded-r-full',
    /**
     * Single day selection
     */
    isStart && isEnd && 'rounded-full',
    isCurrentMonth && !isSelected && !isInRange ? 'hover:bg-gray-100' : '',
    isInPast && 'pointer-events-none text-fg_disabled_light',
  );

  return (
    <button onClick={() => onSelect(date)} disabled={disabled} className={dayClasses}>
      <span
        className={classnames(
          'flex h-[30px] w-full items-center justify-center',
          isInRange && isEndOfWeek && 'rounded-r-full',
          isInRange && isStartOfWeek && 'rounded-l-full',
          isInRange && isFirstDayOfCurrentMonth && 'rounded-l-full',
          isInRange && isLastDayOfCurrentMonth && 'rounded-r-full',
        )}
        style={
          /**
           * Split the background color 50/50 with background-image linear gradient so that we
           * only show right side of background color when its "inRange && isStart"
           */
          (() => {
            if (isInRange && isStart) {
              return { backgroundImage: `linear-gradient(to left, ${surfaceColors.surface_l3} 50%, transparent 50%)` };
            }

            if (isInRange && isEnd) {
              return { backgroundImage: `linear-gradient(to right, ${surfaceColors.surface_l3} 50%, transparent 50%)` };
            }

            if (isInRange) {
              return { backgroundColor: surfaceColors.surface_l3 };
            }

            return null;
          })()
        }>
        <span
          className={classnames(
            (isStart || isEnd) && 'bg-primary flex h-[40px] w-[40px] items-center justify-center rounded-full',
          )}>
          {mDate.format('D')}
        </span>
      </span>
    </button>
  );
};

export default Day;
